import { useMutation, useQueryClient } from '@tanstack/react-query';

import { ClientError } from '~/errors';
import { createLogger } from '~/utils/logging';
import { useHttpClient } from '~/hooks/useHttpClient';
import { REVIEW_ACTION_ITEMS_QUERY_KEY, REVIEW_REMINDERS_QUERY_KEY } from '~/services/review';

import { ActionItemsScorecardType, ActionStatus } from '../types';

const log = createLogger('useActionItemUpdateStatusMutation');

export type ActionItemUpdateStatusRequest = {
  actionItemId: string;
  status: ActionStatus;
};

export type ActionItemUpdateStatusResponse = {
  actionItem: {
    id: string;
    title: string;
    type: ActionItemsScorecardType;
    status: ActionStatus;
  };
};

export function useActionItemUpdateStatusMutation() {
  const httpClient = useHttpClient();
  const queryClient = useQueryClient();

  const { data, error, mutate, reset, status } = useMutation({
    mutationFn: async (request: ActionItemUpdateStatusRequest) => {
      log.debug('Updating action item status', { request });
      const { actionItemId, status } = request;

      const { actionItem } = await httpClient.post<ActionItemUpdateStatusResponse>('/review/updateActionItemStatus', {
        actionItemId,
        status,
      });
      if (actionItem) {
        log.info('Updated action item status successfully', actionItem);
      }
      return { actionItem };
    },
    onSuccess: () => {
      //Invaliate any queries that depend on the reminder
      queryClient.invalidateQueries({ queryKey: [REVIEW_ACTION_ITEMS_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [REVIEW_REMINDERS_QUERY_KEY] });
    },
    onError: (error: Error) => {
      if (error instanceof ClientError) {
        // Ignore client errors.
      } else {
        log.error('Failed to update action item status', error);
      }
    },
  });

  return {
    data,
    error,
    mutate,
    reset,
    status,
  };
}
